import React, { useState, useMemo } from "react";
import PhoneRoundedIcon from "@mui/icons-material/PhoneRounded";
import EmailRoundedIcon from "@mui/icons-material/EmailRounded";
import BusinessRoundedIcon from "@mui/icons-material/BusinessRounded";
import LanguageRoundedIcon from "@mui/icons-material/LanguageRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import FacebookIcon from "@mui/icons-material/Facebook";
import InstagramIcon from "@mui/icons-material/Instagram";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import XIcon from "@mui/icons-material/X";
import InfoRoundedIcon from "@mui/icons-material/InfoRounded";
import PersonAddRoundedIcon from "@mui/icons-material/PersonAddRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import { Card, CardBody } from "components/ui";
import { Employee } from "types/employee";

interface EmployeeProfileProps {
  employee: Employee | null;
  variant?: "preview" | "public";
  defaultSectionsOpen?: boolean;
  isLoading?: boolean;
  error?: string | null;
  licenseExpired?: boolean;
  isDemo?: boolean;
}

export const EmployeeProfile: React.FC<EmployeeProfileProps> = ({
  employee,
  variant = "public",
  defaultSectionsOpen = false,
  isLoading = false,
  error = null,
  licenseExpired = false,
  isDemo = false,
}) => {
  const isExpired =
    licenseExpired || Boolean((employee as any)?.licenseExpired);
  const isDemoAccount = isDemo || Boolean((employee as any)?.isDemo);
  const [sectionsOpen, setSectionsOpen] = useState<{
    contact: boolean;
    company: boolean;
    summary: boolean;
  }>({
    contact: defaultSectionsOpen,
    company: defaultSectionsOpen,
    summary: defaultSectionsOpen,
  });

  const isPreview = variant === "preview";

  const companyData = useMemo(() => {
    if (!employee) return {};
    const anyEmp = employee as any;
    const company = anyEmp?.companyDetails || {};
    return {
      name:
        anyEmp?.companyName ||
        anyEmp?.company ||
        company?.name ||
        "Your Company",
      logo: company?.logo || anyEmp?.logo,
      primaryColor: company?.primaryColor || anyEmp?.primaryColor || "#3b82f6",
      secondaryColor:
        company?.secondaryColor || anyEmp?.secondaryColor || "#6366f1",
      fontColor: company?.fontColor || anyEmp?.fontColor || "#ffffff",
      showLogo: company?.showLogo || anyEmp?.showLogo || false,
      website: anyEmp?.companyWebsite || company?.website,
      phoneNumber: anyEmp?.companyPhone || company?.phoneNumber,
      countryCode: anyEmp?.companyCountryCode || company?.countryCode,
      summary: anyEmp?.summary || company?.summary,
      isFreelancer: company?.isFreelancer || anyEmp?.isFreelancer || false,
      facebookUrl: anyEmp?.facebookUrl || company?.facebookUrl,
      instagramUrl: anyEmp?.instagramUrl || company?.instagramUrl,
      linkedInUrl: anyEmp?.linkedinUrl || company?.linkedInUrl,
      xUrl: anyEmp?.xUrl || company?.xUrl,
    };
  }, [employee]);

  const theme = {
    start: companyData.primaryColor,
    end: companyData.secondaryColor,
    font: companyData.fontColor,
  };

  const avatarSrc = useMemo(() => {
    if (!employee) return "";
    const img = (employee as any)?.image || (employee as any)?.imageBase64;
    if (!img || typeof img !== "string") return "";
    return img.startsWith("data:") ? img : `data:image/jpeg;base64,${img}`;
  }, [employee]);

  const employeeName = useMemo(() => {
    if (!employee) return "Unknown Employee";
    const parts = [
      employee.firstName,
      employee.middleName,
      employee.lastName,
    ].filter(Boolean); // remove empty/undefined values

    return parts.join(" ") || employee.name || "Unknown Employee";
  }, [employee]);

  const toggle = (key: "contact" | "company" | "summary") => {
    setSectionsOpen((prev) => {
      const isOpening = !prev[key];
      if (!isOpening) {
        return { contact: false, company: false, summary: false };
      }
      return {
        contact: key === "contact",
        company: key === "company",
        summary: key === "summary",
      };
    });
  };

  const handleAddContact = () => {
    if (!employee) return;

    const vCard = generateVCard(employee);

    if (navigator.share) {
      navigator
        .share({
          title: `Add ${employeeName} to Contacts`,
          text: `Contact information for ${employeeName}`,
          url: `data:text/vcard;charset=utf-8,${encodeURIComponent(vCard)}`,
        })
        .catch((error) => {
          downloadVCard(vCard, employeeName);
        });
    } else {
      downloadVCard(vCard, employeeName);
    }
  };

  const generateVCard = (emp: Employee): string => {
    const name = `${emp.firstName || ""} ${emp.middleName || ""} ${
      emp.lastName || ""
    }`.trim();
    const fullPhone =
      emp.countryCode && emp.mobileNumber
        ? `+${emp.countryCode}${emp.mobileNumber}`
        : emp.mobileNumber;

    let vCard = "BEGIN:VCARD\n";
    vCard += "VERSION:3.0\n";
    vCard += `FN:${name}\n`;

    if (emp.firstName)
      vCard += `N:${emp.lastName || ""};${emp.firstName};${
        emp.middleName || ""
      };;\n`;
    if (emp.title) vCard += `TITLE:${emp.title}\n`;
    if (emp.email) vCard += `EMAIL:${emp.email}\n`;
    if (fullPhone) vCard += `TEL:${fullPhone}\n`;

    if (companyData.name) vCard += `ORG:${companyData.name}\n`;
    if (companyData.website) vCard += `URL:${companyData.website}\n`;
    if (companyData.phoneNumber) {
      const companyPhone = companyData.countryCode
        ? `+${companyData.countryCode}${companyData.phoneNumber}`
        : companyData.phoneNumber;
      vCard += `TEL;TYPE=WORK:${companyPhone}\n`;
    }

    vCard += "END:VCARD";
    return vCard;
  };

  const downloadVCard = (vCard: string, name: string) => {
    const blob = new Blob([vCard], { type: "text/vcard;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${name.replace(/\s+/g, "_")}.vcf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const ensureHttps = (url: string | undefined): string | undefined => {
    if (!url) return undefined;
    if (url.startsWith("http://") || url.startsWith("https://")) {
      return url;
    }
    return `https://${url}`;
  };

  const socialLinks = useMemo(() => {
    if (!employee) return [];
    return [
      companyData.facebookUrl && {
        label: "Facebook",
        href: ensureHttps(companyData.facebookUrl),
        Icon: FacebookIcon,
      },
      companyData.instagramUrl && {
        label: "Instagram",
        href: ensureHttps(companyData.instagramUrl),
        Icon: InstagramIcon,
      },
      companyData.linkedInUrl && {
        label: "LinkedIn",
        href: ensureHttps(companyData.linkedInUrl),
        Icon: LinkedInIcon,
      },
      companyData.xUrl && {
        label: "X",
        href: ensureHttps(companyData.xUrl),
        Icon: XIcon,
      },
      companyData.isFreelancer &&
        companyData.website && {
          label: "Website",
          href: ensureHttps(companyData.website),
          Icon: LanguageRoundedIcon,
        },
    ].filter(Boolean) as {
      label: string;
      href: string;
      Icon: typeof FacebookIcon;
    }[];
  }, [employee, companyData]);

  if (!isExpired && (error || (!employee && !isLoading))) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="max-w-md w-full mx-4">
          <CardBody>
            <div className="text-center">
              <div className="text-red-600 text-6xl mb-4">⚠️</div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                Profile Not Found
              </h2>
              <p className="text-gray-600 mb-4">
                {error ||
                  "The employee profile you are looking for does not exist or has been removed."}
              </p>
            </div>
          </CardBody>
        </Card>
      </div>
    );
  }

  // Loading state for public variant
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="max-w-md w-full mx-4">
          <CardBody>
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading profile...</p>
            </div>
          </CardBody>
        </Card>
      </div>
    );
  }

  const containerClass = isPreview
    ? "bg-gray-50 flex items-center justify-center p-2"
    : "min-h-screen bg-gray-50 flex items-center justify-center p-4";

  const cardClass = isPreview
    ? "max-w-sm w-full min-w-[320px]"
    : "max-w-sm w-full";

  const cardStyle = isPreview
    ? "bg-white rounded-2xl shadow-lg overflow-hidden"
    : "bg-white rounded-3xl shadow-xl overflow-hidden";

  const headerHeight = "h-48";
  const headerPadding = isPreview ? "px-4 py-3" : "px-6 py-4";

  const avatarSize = isPreview ? "w-20 h-20" : "w-28 h-28";
  const avatarBorder = isPreview ? "border-3" : "border-4";
  const avatarMargin = isPreview ? "-mt-12" : "-mt-16";
  const contentPadding = isPreview ? "px-4 pb-4" : "px-6 pb-6";

  const buttonClass = isPreview
    ? "px-3 py-1.5 text-xs font-medium rounded-lg text-white transition-all duration-200 flex items-center gap-1"
    : "px-4 py-2 text-sm font-medium rounded-lg text-white transition-all duration-200 flex items-center gap-2";

  const sectionPadding = isPreview ? "px-3 py-2" : "px-4 py-3";
  const sectionContentPadding = isPreview ? "px-3 pb-3" : "px-4 pb-4";
  const sectionSpacing = isPreview ? "space-y-2" : "space-y-3";

  const socialIconSize = isPreview ? "w-10 h-10" : "w-12 h-12";
  const socialMargin = isPreview ? "mt-4" : "mt-6";
  const socialGap = isPreview ? "gap-3" : "gap-4";

  const IDS_URL = process.env.REACT_APP_IDS_URL || "https://www.ids.com.lb";
  const QRCARD_URL =
    process.env.REACT_APP_QRCARD_URL ||
    (typeof window !== "undefined" ? window.location.origin : "/");

  return (
    <div className={containerClass}>
      <div className={cardClass}>
        {isExpired && (
          <div className="absolute inset-0 z-50 pointer-events-none select-none flex items-center justify-center">
            <div 
              className="flex flex-col items-center justify-center rotate-[30deg] opacity-[0.4]  scale-180"
              style={{ width: '100%', height: '200%' }}
            >
              {Array.from({ length: 5 }).map((_, i) => (
                <span key={i} className="mt-3 mb-12 text-red-600 font-black text-4xl uppercase tracking-tighter">
                  EXPIRED
                </span>
              ))}
            </div>
          </div>
        )}
        <div className={cardStyle}>
          <div
            className={`relative ${headerHeight} ${headerPadding}`}
            style={{
              background: `linear-gradient(135deg, ${theme.start} 0%, ${theme.end} 100%)`,
              position: "relative",
            }}
          >
            <div className="absolute inset-0 overflow-hidden">
              <div
                className="absolute top-2 left-4 w-8 h-8 rounded-full opacity-20"
                style={{ backgroundColor: "transparent" }}
              ></div>
              <div
                className="absolute top-8 right-8 w-6 h-6 rounded-full opacity-30"
                style={{ backgroundColor: "transparent" }}
              ></div>
              <div
                className="absolute bottom-4 left-8 w-10 h-10 rounded-full opacity-15"
                style={{ backgroundColor: "transparent" }}
              ></div>
              <div
                className="absolute bottom-2 right-4 w-4 h-4 rounded-full opacity-25"
                style={{ backgroundColor: "transparent" }}
              ></div>
            </div>

            {companyData.showLogo && companyData.logo && (
              <div className="absolute top-4 right-4">
                <img
                  src={
                    companyData.logo?.startsWith("data:")
                      ? companyData.logo
                      : `data:image/png;base64,${companyData.logo || ""}`
                  }
                  alt="Company Logo"
                  className="w-25 h-24 object-contain bg-transparent"
                />
              </div>
            )}

            {companyData.showLogo && (
              <div
                className="absolute bottom-4 right-4 text-sm font-medium"
                style={{ color: "white" }}
              >
                {companyData.name || "Your Company"}
              </div>
            )}
          </div>

          <div className={`relative ${contentPadding} ${avatarMargin}`}>
            <div className="flex flex-col items-start">
              <div
                className={`${avatarSize} rounded-full overflow-hidden ${avatarBorder} border-white shadow-md bg-white mb-2`}
              >
                {avatarSrc ? (
                  <img
                    src={avatarSrc}
                    alt={employeeName}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-400 text-xl font-semibold bg-gray-100">
                    {employeeName?.charAt(0) || "J"}
                  </div>
                )}
              </div>

              <div className="text-left mb-2">
                <h1
                  className={`${
                    isPreview ? "text-base" : "text-lg"
                  } font-bold mb-1`}
                  style={{ color: theme.font }}
                >
                  {employeeName}
                </h1>
                {employee?.title && (
                  <p
                    className={`${isPreview ? "text-xs" : "text-sm"}`}
                    style={{ color: theme.font }}
                  >
                    {employee.title}
                  </p>
                )}
              </div>

              <div className="flex flex-wrap items-center gap-3 mt-2">
                <button
                  className={buttonClass}
                  style={isPreview ? {} : { backgroundColor: theme.start }}
                  onClick={handleAddContact}
                  disabled={isExpired}
                >
                  <PersonAddRoundedIcon fontSize="small" />
                  Add Contact
                </button>
                {isExpired && (
                  <span className="inline-flex items-center px-3 py-2 rounded-full bg-red-600 text-white text-xs font-semibold uppercase tracking-wide shadow">
                    Subscription expired
                  </span>
                )}
                {/* {isDemoAccount && (
                    <span className="inline-flex items-center px-4 py-2 rounded-full bg-amber-500 text-white text-xs font-semibold uppercase tracking-wide shadow">
                      Demo Subscription
                    </span>
                  )} */}
              </div>
            </div>
          </div>

          <div className={`${contentPadding} ${sectionSpacing}`}>
            <div className="bg-gray-50 rounded-xl overflow-hidden">
              <button
                onClick={() => toggle("contact")}
                aria-expanded={sectionsOpen.contact}
                className={`w-full flex items-center justify-between ${sectionPadding} focus:outline-none hover:bg-gray-100 transition-colors`}
              >
                <div className="flex items-center gap-3">
                  <span
                    className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-white shadow-sm"
                    style={{ color: theme.start }}
                  >
                    <PersonRoundedIcon fontSize="small" />
                  </span>
                  <span
                    className="text-sm font-semibold"
                    style={{ color: theme.font }}
                  >
                    Contact
                  </span>
                </div>
                <ExpandMoreRoundedIcon
                  style={{ color: theme.start }}
                  className={`transition-transform ${
                    sectionsOpen.contact ? "rotate-180" : ""
                  }`}
                />
              </button>
              {sectionsOpen.contact && (
                <div className={sectionContentPadding}>
                  <div className={sectionSpacing}>
                    {employee?.mobileNumber && (
                      <div className="flex items-center gap-3">
                        <div
                          className="w-6 h-6 flex items-center justify-center"
                          style={{ color: theme.start }}
                        >
                          <PhoneRoundedIcon fontSize="small" />
                        </div>
                        <a
                          href={
                            isPreview
                              ? `tel:${employee.mobileNumber}`
                              : `tel:+${employee.countryCode}${employee.mobileNumber}`
                          }
                          className="text-sm hover:text-gray-900 transition-colors"
                          style={{ color: theme.font }}
                        >
                          {isPreview
                            ? employee.mobileNumber
                            : `(${employee.countryCode}) ${employee.mobileNumber}`}
                        </a>
                      </div>
                    )}
                    {employee?.email && (
                      <div className="flex items-center gap-3">
                        <div
                          className="w-6 h-6 flex items-center justify-center"
                          style={{ color: theme.start }}
                        >
                          <EmailRoundedIcon fontSize="small" />
                        </div>
                        <a
                          href={`mailto:${employee.email}`}
                          className="text-sm hover:text-gray-900 transition-colors break-all"
                          style={{ color: theme.font }}
                        >
                          {employee.email}
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {!companyData.isFreelancer && (
              <div className="bg-gray-50 rounded-xl overflow-hidden">
                <button
                  onClick={() => toggle("company")}
                  aria-expanded={sectionsOpen.company}
                  className={`w-full flex items-center justify-between ${sectionPadding} focus:outline-none hover:bg-gray-100 transition-colors`}
                >
                  <div className="flex items-center gap-3">
                    <span
                      className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-white shadow-sm"
                      style={{ color: theme.start }}
                    >
                      <BusinessRoundedIcon fontSize="small" />
                    </span>
                    <span
                      className="text-sm font-semibold"
                      style={{ color: theme.font }}
                    >
                      Company
                    </span>
                  </div>
                  <ExpandMoreRoundedIcon
                    style={{ color: theme.start }}
                    className={`transition-transform ${
                      sectionsOpen.company ? "rotate-180" : ""
                    }`}
                  />
                </button>
                {sectionsOpen.company && (
                  <div className={sectionContentPadding}>
                    <div className={sectionSpacing}>
                      <div className="flex items-center gap-3">
                        <div
                          className="w-6 h-6 flex items-center justify-center"
                          style={{ color: theme.start }}
                        >
                          <BusinessRoundedIcon fontSize="small" />
                        </div>
                        <span className="text-sm" style={{ color: theme.font }}>
                          {companyData.name ||
                            (isPreview
                              ? "Your Company"
                              : "Integrated Digital Systems")}
                        </span>
                      </div>
                      {companyData.phoneNumber && (
                        <div className="flex items-center gap-3">
                          <div
                            className="w-6 h-6 flex items-center justify-center"
                            style={{ color: theme.start }}
                          >
                            <PhoneRoundedIcon fontSize="small" />
                          </div>
                          <a
                            href={`tel:${companyData.phoneNumber}`}
                            className="text-sm hover:text-gray-900 transition-colors"
                            style={{ color: theme.font }}
                          >
                            {companyData.countryCode
                              ? `(${companyData.countryCode}) `
                              : ""}
                            {companyData.phoneNumber}
                          </a>
                        </div>
                      )}
                      {companyData.website && (
                        <div className="flex items-center gap-3">
                          <div
                            className="w-6 h-6 flex items-center justify-center"
                            style={{ color: theme.start }}
                          >
                            <LanguageRoundedIcon fontSize="small" />
                          </div>
                          <a
                            href={companyData.website}
                            target="_blank"
                            rel="noreferrer"
                            className="text-sm hover:text-gray-900 transition-colors break-all"
                            style={{ color: theme.font }}
                          >
                            {companyData.website
                              .replace(/^https?:\/\//, "")
                              .replace(/\/$/, "")}
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
            <div className="bg-gray-50 rounded-xl overflow-hidden">
              <button
                onClick={() => toggle("summary")}
                aria-expanded={sectionsOpen.summary}
                className={`w-full flex items-center justify-between ${sectionPadding} focus:outline-none hover:bg-gray-100 transition-colors`}
              >
                <div className="flex items-center gap-3">
                  <span
                    className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-white shadow-sm"
                    style={{ color: theme.start }}
                  >
                    <InfoRoundedIcon fontSize="small" />
                  </span>
                  <span
                    className="text-sm font-semibold"
                    style={{ color: theme.font }}
                  >
                    Summary
                  </span>
                </div>
                <ExpandMoreRoundedIcon
                  style={{ color: theme.start }}
                  className={`transition-transform ${
                    sectionsOpen.summary ? "rotate-180" : ""
                  }`}
                />
              </button>
              {sectionsOpen.summary && (
                <div className={sectionContentPadding}>
                  {companyData.summary ? (
                    <div
                      className="text-sm whitespace-pre-line opacity-90"
                      style={{ color: theme.font }}
                    >
                      {companyData.summary}
                    </div>
                  ) : (
                    <p
                      className={`text-sm ${
                        isPreview ? "text-gray-500 opacity-70" : "opacity-70"
                      }`}
                      style={isPreview ? {} : { color: theme.font }}
                    >
                      {isPreview
                        ? "This is a sample summary for your company. You can customize this text in the company form."
                        : "No summary available."}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {socialLinks.length > 0 && (
          <div
            className={`flex items-center justify-center ${socialGap} ${socialMargin}`}
          >
            {socialLinks.map(({ label, href, Icon }) => (
              <a
                key={label}
                aria-label={label}
                href={href}
                target="_blank"
                rel="noreferrer"
                className={`${socialIconSize} rounded-full flex items-center justify-center bg-white shadow-md hover:shadow-lg transition-all duration-200 hover:scale-105`}
                style={{ color: theme.start }}
              >
                <Icon />
              </a>
            ))}
          </div>
        )}
        {/* Footer: © YEAR QRCard • Powered by IDS */}
        <div className="mt-4 text-center text-xs text-gray-500">
          <span>© {new Date().getFullYear()} </span>
          <a
            href={QRCARD_URL}
            target="_blank"
            rel="noreferrer"
            className="font-semibold hover:underline"
            style={{ color: theme.start }}
          >
            YourQcard
          </a>
          <span> • Powered by </span>
          <a
            href={IDS_URL}
            target="_blank"
            rel="noreferrer"
            className="font-semibold hover:underline"
            style={{ color: theme.start }}
          >
            IDS
          </a>
        </div>
      </div>
    </div>
  );
};

export default EmployeeProfile;
